import type { ParsedWordSegmentWithDirection, TextDirection } from '../types';
import { isRTLLevel, isLTRLevel } from '../utils/level-utils';

/**
 * Phase 4: Neutral Word Resolution
 *
 * Resolve neutral words (punctuation, numbers) based on surrounding words
 * and embedding levels.
 */
export const resolveNeutralWords = (
  words: Array<ParsedWordSegmentWithDirection>,
  paragraphDirection: TextDirection,
): Array<ParsedWordSegmentWithDirection> => {
  return words.map((word, index) => {
    if (word.direction !== 'neutral') {
      return word;
    }

    const prevWord = words[index - 1];
    const nextWord = words[index + 1];

    // For spaces and punctuation, use embedding level to determine direction
    // If embedding level is odd (RTL context), prefer RTL
    if (isRTLLevel(word.embeddingLevel)) {
      return { ...word, direction: 'rtl' };
    } else if (isLTRLevel(word.embeddingLevel) && word.embeddingLevel > 0) {
      return { ...word, direction: 'ltr' };
    }

    // If surrounded by same direction, adopt it
    if (
      prevWord &&
      nextWord &&
      prevWord.direction === nextWord.direction &&
      prevWord.direction !== 'neutral'
    ) {
      return { ...word, direction: prevWord.direction };
    }

    // Inherit from preceding strong word
    if (prevWord && prevWord.direction !== 'neutral') {
      return { ...word, direction: prevWord.direction };
    }

    // Inherit from following strong word
    if (nextWord && nextWord.direction !== 'neutral') {
      return { ...word, direction: nextWord.direction };
    }

    // Use paragraph direction as fallback
    return { ...word, direction: paragraphDirection };
  });
};
